import { useEffect, useRef, useState } from "react";
import { getWsUrl } from "@/lib/api";

const isDev = process.env.NODE_ENV === "development";
const logDev = (...args) => { if (isDev) console.warn("[ws]", ...args); };

/**
 * Subscribes to /api/ws/{agencyId} and calls onMessage(json) for each event.
 *
 * Reconnects with exponential backoff (1s, 2s, 4s, … capped at 10s).
 * Returns a tuple [status, lastError] so callers can render a network indicator.
 */
export function useAgencyWS(agencyId, onMessage) {
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;
  const [status, setStatus] = useState(agencyId ? "connecting" : "idle");

  useEffect(() => {
    if (!agencyId) { setStatus("idle"); return; }
    let alive = true;
    let pingInterval = null;
    setStatus("connecting");

    const connect = () => {
      if (!alive) return;
      const ws = new WebSocket(getWsUrl(agencyId));
      wsRef.current = ws;
      ws.onopen = () => {
        retryRef.current = 0;
        setStatus("open");
        pingInterval = setInterval(() => {
          if (ws.readyState === 1) ws.send("ping");
        }, 30000);
      };
      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          handlerRef.current?.(data);
        } catch (e) {
          logDev("parse error", e, evt.data);
        }
      };
      ws.onclose = () => {
        if (pingInterval) clearInterval(pingInterval);
        if (!alive) return;
        setStatus("reconnecting");
        const delay = Math.min(1000 * 2 ** retryRef.current, 10000);
        retryRef.current += 1;
        setTimeout(connect, delay);
      };
      ws.onerror = (e) => {
        logDev("socket error", e?.message || e);
        try { ws.close(); } catch (closeErr) { logDev("close after error failed", closeErr); }
      };
    };

    connect();
    return () => {
      alive = false;
      if (pingInterval) clearInterval(pingInterval);
      try { wsRef.current?.close(); } catch (e) { logDev("cleanup close failed", e); }
    };
  }, [agencyId]);

  return status;
}

/** Speak text using Web Speech API (French). Falls back silently if unavailable. */
export function speakFR(text) {
  try {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "fr-FR";
    u.rate = 0.92;
    u.pitch = 1.0;
    u.volume = 1.0;
    window.speechSynthesis.speak(u);
  } catch (e) {
    logDev("speakFR failed", e);
  }
}

/** Short notification beep using Web Audio (no asset file). */
export function playBeep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const tones = [880, 1320, 1760];
    tones.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + i * 0.18);
      gain.gain.setValueAtTime(0.0001, now + i * 0.18);
      gain.gain.exponentialRampToValueAtTime(0.25, now + i * 0.18 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.18 + 0.16);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.18);
      osc.stop(now + i * 0.18 + 0.18);
    });
    setTimeout(() => ctx.close(), 1200);
  } catch (e) {
    logDev("playBeep failed", e);
  }
}
